import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import type {
  UseCurrentPlanQuery,
  UseCurrentPlanQueryVariables,
} from 'src/lib/graphqlTypes/types';

interface Args extends UseCurrentPlanQueryVariables {
  skip?: boolean;
}

export const USE_CURRENT_PLAN_QUERY = gql<
  UseCurrentPlanQuery,
  UseCurrentPlanQueryVariables
>/* GraphQL */ `
  query UseCurrentPlanQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      limits {
        maxRangeInDays
        maxAuditInDays
        maxRangeInDaysForChecks
        maxRequestsPerMonth
      }
      currentPlan {
        id
        kind
        tier
        provider
      }
    }
  }
`;

/**
 * @deprecated Avoid using this custom hook to query for data as it will lead to over-fetching. You should query for the data you need directly.
 */
export function useCurrentPlan({ skip = false, ...variables }: Args) {
  const currentPlanResult = useQuery<
    UseCurrentPlanQuery,
    UseCurrentPlanQueryVariables
  >(USE_CURRENT_PLAN_QUERY, {
    variables,
    skip,
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  return useMemo(() => {
    const account = currentPlanResult.data?.account;
    const currentPlan = account?.currentPlan;
    const limits = account?.limits;

    return {
      currentPlan,
      limits,
      loading: currentPlanResult.loading,
      error: currentPlanResult.error,
    };
  }, [currentPlanResult]);
}
